import React from "react"

import { Box, Button, Divider, List, Menu, Typography } from "@mui/material"
import { Notifications } from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { Link } from "react-router-dom"

import { Trans } from "@lingui/react"

import { Action } from "models"
import NotificationListItem, { NotificationItemData } from "./notification-list-item"

const NotificationContainer = styled(Box)(({ theme }) => ({
	minWidth: "320px",
	maxWidth: "400px",
}))

const NotificationHeader = styled(Typography)(({ theme }) => ({
	padding: theme.spacing(2, 2, 1, 2),
	fontSize: "1rem",
	fontWeight: 600,
	color: theme.palette.text.primary,
	borderBottom: `1px solid ${theme.palette.divider}`,
	marginBottom: theme.spacing(1),
}))

const NotificationList = styled(List)(({ theme }) => ({
	width: "100%",
	maxHeight: "60vh",
	overflow: "auto",
	padding: 0,
	"& .MuiListItemButton-root": {
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
}))

const EmptyNotificationContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: theme.spacing(4, 2),
	minHeight: "200px",
	color: theme.palette.text.secondary,
}))

const ViewAllButtonContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(1, 2, 2, 2),
}))

interface NotificationMenuProps {
	anchorEl: HTMLElement | null
	open: boolean
	onClose: () => void
	notificationsTotal: number
	hasNotifications: boolean
	unreadProposalNotifications: NotificationItemData[]
	unreadOtherNotifications: NotificationItemData[]
	readNotifications: NotificationItemData[]
	onOpenDetails: (notification: Action) => void
	listRef?: React.RefObject<any>
}

const NotificationMenu: React.FC<NotificationMenuProps> = ({
	anchorEl,
	open,
	onClose,
	notificationsTotal,
	hasNotifications,
	unreadProposalNotifications,
	unreadOtherNotifications,
	readNotifications,
	onOpenDetails,
	listRef,
}) => {
	return (
		<Menu
			id="notification-appbar"
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
			}}
			keepMounted
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			open={open}
			onClose={onClose}
			disableScrollLock
			MenuListProps={{ sx: { padding: 0 } }}
			PaperProps={{
				sx: {
					mt: 1,
					boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
					borderRadius: 2,
				},
			}}
		>
			<NotificationContainer>
				<NotificationHeader>
					Notifiche ({notificationsTotal})
				</NotificationHeader>

				{hasNotifications ? (
					<>
						<NotificationList ref={listRef}>
							{unreadProposalNotifications.length > 0 && (
								<Box sx={{ pb: (unreadOtherNotifications.length > 0 || readNotifications.length > 0) ? 1 : 0 }}>
									{unreadProposalNotifications.map((notification) => (
										<NotificationListItem
											key={notification.notification.id}
											{...notification}
											onOpenDetails={onOpenDetails}
										/>
									))}
								</Box>
							)}
							{unreadProposalNotifications.length > 0 && unreadOtherNotifications.length > 0 && (
								<Divider sx={{ mx: 2, my: 1 }} />
							)}
							{unreadOtherNotifications.length > 0 && (
								<Box sx={{ py: (unreadProposalNotifications.length > 0 || readNotifications.length > 0) ? 1 : 0 }}>
									{unreadOtherNotifications.map((notification) => (
										<NotificationListItem
											key={notification.notification.id}
											{...notification}
											onOpenDetails={onOpenDetails}
										/>
									))}
								</Box>
							)}
							{(unreadProposalNotifications.length > 0 || unreadOtherNotifications.length > 0) && readNotifications.length > 0 && (
								<Divider sx={{ mx: 2, my: 1 }} />
							)}
							{readNotifications.length > 0 && (
								<Box sx={{ pt: (unreadProposalNotifications.length > 0 || unreadOtherNotifications.length > 0) ? 1 : 0 }}>
									{readNotifications.map((notification) => (
										<NotificationListItem
											key={notification.notification.id}
											{...notification}
											onOpenDetails={onOpenDetails}
										/>
									))}
								</Box>
							)}
						</NotificationList>
						<ViewAllButtonContainer>
							<Button
								component={Link}
								to="/notifications"
								variant="contained"
								color="secondary"
								disableElevation
								onClick={onClose}
								sx={{
									width: "100%",
									textTransform: "none",
									fontSize: "0.9rem",
									fontWeight: 500,
									py: 1,
								}}
							>
								Mostra tutte
							</Button>
						</ViewAllButtonContainer>
					</>
				) : (
					<EmptyNotificationContainer>
						<Notifications sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
						<Typography variant="body2" textAlign="center">
							<Trans id="actions.no-notification">
								Nessuna notifica trovata
							</Trans>
						</Typography>
					</EmptyNotificationContainer>
				)}
			</NotificationContainer>
		</Menu>
	)
}

export default NotificationMenu
